<template>
  <div class="signature-view">
    <div class="columns">
      <div class="column is-half">
        <p>
          <b v-if="form?.signcustodian">
            Sign your name, then "for" and then the minor's name.
          </b>
          <b v-else>
            Please sign exactly as your name appears at the top of the first page.
          </b>
        </p>
        <br />
        <canvas ref="canvasEl" class="sig-canvas" />
        <div v-if="!disabled" class="sig-controls">
          <Button label="Undo" size="small" severity="secondary" @click="undo" />
          <Button label="Clear" size="small" severity="secondary" @click="clear" />
        </div>
      </div>
      <div class="column is-half">
        <div class="field">
          <Checkbox
            v-model="form.sigx"
            :disabled="disabled"
            :binary="true"
            input-id="sigx"
          />
          <label for="sigx" style="margin-left: 8px;">
            I am signing my name with an "X".
          </label>
        </div>
        <div v-if="form?.sigx" class="sigx-fields">
          <p>Two witnesses are required when you sign your name with an "X".</p>
          <div class="p-field">
            <label>Witness</label>
            <InputText v-model="form.witness1" :disabled="disabled" fluid />
          </div>
          <div class="p-field">
            <label>Witness</label>
            <InputText v-model="form.witness2" :disabled="disabled" fluid />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import InputText from 'primevue/inputtext'
import SignaturePad from 'signature_pad'

const props = defineProps({
  component: { type: Object, required: true },
  shareholder: { type: Object, default: () => ({}) },
  mode: { type: String, default: 'vote' },
  form: { type: Object, default: null },
  shareValue: { type: Number, default: 2 },
  ballotComponents: { type: Array, default: () => [] },
})

const emit = defineEmits(['register-signature-pad'])

const disabled = computed(() => props.mode !== 'vote')
const canvasEl = ref(null)
let pad = null

function resizeCanvas() {
  if (!canvasEl.value) return
  const ratio = Math.max(window.devicePixelRatio || 1, 1)
  canvasEl.value.width = canvasEl.value.offsetWidth * ratio
  canvasEl.value.height = canvasEl.value.offsetHeight * ratio
  canvasEl.value.getContext('2d').scale(ratio, ratio)
  if (pad) pad.clear()
}

onMounted(() => {
  if (!canvasEl.value) return
  pad = new SignaturePad(canvasEl.value, { backgroundColor: 'rgba(255,255,255,0)' })
  resizeCanvas()
  window.addEventListener('resize', resizeCanvas)
  if (disabled.value) pad.off()
  emit('register-signature-pad', pad)
})

onUnmounted(() => {
  window.removeEventListener('resize', resizeCanvas)
})

function undo() {
  if (!pad) return
  const data = pad.toData()
  data.pop()
  pad.fromData(data)
}

function clear() {
  if (pad) pad.clear()
}
</script>

<style scoped>
.signature-view {
  padding: 10px 0;

  .sig-canvas {
    width: 100%;
    height: 250px;
    border: 2px solid #efefef;
    border-radius: 10px;
    display: block;
    /* Medium-gray in dark mode keeps dark ink visible without a
       blinding white panel in the middle of the ballot. */
    background: var(--sig-bg, #fff);
  }

  .sig-controls {
    margin-top: 8px;
    display: flex;
    gap: 8px;
  }

  .p-field {
    margin-bottom: 10px;

    label {
      display: block;
      font-size: 0.85rem;
      color: #555;
      margin-bottom: 4px;
    }
  }

  .sigx-fields {
    margin-top: 12px;
  }
}

:deep(.p-inputtext) {
  width: 100%;
}
</style>
